<template>
  <div class="product">
    <p-product-detail
      v-if="product && hasNoRedirection"
      :key="productId"
      :cooperative-name="currentCooperativeName"
      :is-authenticated="isAuthenticated"
      :is-crop-protection-allowed="isCropProtectionAllowed"
      :is-explosives-precursor-allowed="isExplosivesPrecursorAllowed"
      :product="product"
      :profile="currentProfile"
      :show-technical-sales-contact="true"
      :supplier-related-product-count="supplierRelatedProductCount"
      :supplier-related-products="enrichedSupplierRelatedProducts"
      :similar-products="similarProducts"
      :frequently-bought-together-products="frequentlyBoughtTogetherProducts"
      :banner="banner"
      data-test-product-detail
      @go-to-product-search="goToProductSearch"
      @login="login()"
      @search-active-substance="searchActiveSubstance"
    />
    <div class="container container--full-width-on-mobile">
      <c-carousel-article
        v-if="articleThumbnail"
        :partner-id="currentCooperativeId"
        :article-thumbnails="articleThumbnail"
        :truncate-title="110"
        :is-authenticated="isAuthenticated"
        class="product__push-article"
        data-test-carousel
        item-margin="1.2rem"
        :block-origin="'Page Produit carrousel'"
      />
    </div>
    <p-not-found v-if="!error && !product" data-test-product-loading :message="i18n._('Chargement...')" />
    <p-not-found v-if="error && !product" :message="i18n._('Produit inexistant')" />
  </div>
</template>
<script lang="ts">
import type { CooperativeProduct } from '../services/product'
import { ProductRedirectError } from '../services/product'
import { mapCooperativeProductToProductHit } from '../services/product.mapper'
import { productSEODescription } from '@b2ag/utils/src/SEO/description'
import { productSEOTitle } from '@b2ag/utils/src/SEO/title'
import type { ProductHit, SearchResult } from '@b2ag/search'
import { provideSendProductInfoRequest } from '../composables/useSendProductInfoRequest'
import { useRoute, useRouter } from 'vue-router'
import { useProduct } from '@b2ag/catalog/src/useProduct'
import PNotFound from '@b2ag/design-system/src/presenters/PNotFound/PNotFound.vue'
import PProductDetail from '../presenters/PProductDetail/PProductDetail.vue'
import { SUPPLIER_REFINEMENT } from '@b2ag/search/src/presenters/PAppSearch/constants'
import { useStore } from 'vuex'
import { computed, defineComponent, inject, onBeforeMount, onUnmounted, onUpdated, ref, watch } from 'vue'
import VueScrollTo from 'vue-scrollto'
import type { ArticleThumbnails } from '@b2ag/articles/src/domain/ArticleThumbnails'
import type { FullProduct } from '../services/shop.business'
import { sendProductInfoRequestUseCase } from '../domain/send-product-info-request/dependency-injection'
import type { ProductInfoRequestPayload } from '../domain/send-product-info-request/domain/ProductInfoRequestPayload'
import { articleUseCase } from '@b2ag/articles'
import CCarouselArticle from '@b2ag/articles/src/application/components/CCarouselArticle/CCarouselArticle.vue'
import { currentAppBrand } from '@b2ag/app-brands'
import { useRedirectUrl } from '@b2ag/redirect-url'
import { i18n } from '@b2ag/locale'
import { getBannersForProductSheetUseCase } from '@b2ag/marketing'
import { useProductAnalytics } from '../composables/useProductAnalytics'
import { getConfig } from '@b2ag/micro-front-tools'
import { useHead } from '#imports'

const DEFAULT_PRODUCT_ID = 'ID_NOT_FOUND'

export default defineComponent({
  name: 'VProduct',
  components: {
    PProductDetail,
    PNotFound,
    CCarouselArticle,
  },
  beforeRouteEnter: async (to, from, next) => {
    try {
      const { beforeRouteEnter } = useRedirectUrl()
      return beforeRouteEnter(to, from, next)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error in beforeRouteEnter', err)
      next()
    }
  },
  setup() {
    const error = ref(false)
    const productViewedEventFired = ref(false)
    const articleThumbnail = ref<ArticleThumbnails | null>(null)
    const supplierRelatedProducts = ref<SearchResult['hits']>([])
    const supplierRelatedProductCount = ref(0)
    const hasVerifiedSlugRedirection = ref(false)
    const hasNoRedirection = ref(false)
    const banner = ref()

    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const { sendProductViewedToAnalytics } = useProductAnalytics()

    const user = computed(() => store.getters.user)
    const currentProfile = computed(() => store.getters.currentProfile)
    const currentCooperativeId = computed(() => store.getters.currentCooperativeId)
    const isAuthenticated = computed(() => store.getters.isAuthenticated)
    const currentCooperativeName = computed(() => store.getters.currentCooperativeName)
    const isCropProtectionAllowed = computed(() => store.getters.isCropProtectionAllowed)
    const isExplosivesPrecursorAllowed = computed(() => store.getters.isExplosivesPrecursorAllowed)
    const currentAddress = computed(() => store.getters['addresses/currentAddress'])
    const productId = computed(() => {
      const slug = route.params.productSlug as string
      const parts = slug.split('--')
      return parts[parts.length - 1] || DEFAULT_PRODUCT_ID
    })
    const { product } = useProduct(productId.value)
    const algoliaProducts = computed(() => {
      return store.getters['shop/getAlgoliaProductsByIds']([productId.value])
    })
    const enrichedSupplierRelatedProducts = computed(() => {
      return store.getters['shop/getSupplierProducts'](productId.value)
    })
    const similarProducts = computed(() => {
      return isAuthenticated.value ? store.getters['shop/getSimilarProducts'](productId.value) : []
    })
    const frequentlyBoughtTogetherProducts = computed(() => {
      return store.getters['shop/getFrequentlyBoughtTogetherProducts'](productId.value)
    })
    const userSegments = computed(() => store.getters['cart/userSegments'])

    async function getProduct() {
      let isCooperative
      if (!productId.value) {
        error.value = true
        return
      }
      try {
        error.value = false
        await store.dispatch('shop/fetchProduct', { productId: productId.value })
        isCooperative = true

        await Promise.all([
          store.dispatch('shop/setAssociatedProducts', {
            productId: productId.value,
            membershipNumber: store.getters.currentMembershipNumber,
          }),
          store.dispatch('stock/fetch', { variants: product.value!.variants }),
        ])
      } catch (_: any) {
        if (!isCooperative) {
          try {
            await store.dispatch('shop/fetchNationalProduct', { productId: productId.value })
          } catch (err: any) {
            if (err instanceof ProductRedirectError) {
              router?.push(err.redirectUrl)
            }
            error.value = true
          }
        }
      }
    }

    function updateVisitedProductsList() {
      if (product.value) store.dispatch('addVisitedProduct', mapCooperativeProductToProductHit(product.value))
    }

    function login() {
      router?.push({ name: 'login' })
    }

    const getSlugFromCategories = inject('getSlugFromCategories') as (categories: string[]) => string

    function goToProductSearch({ categoryCode, supplierName }) {
      const categories = product.value?.categories?.find((category) =>
        category.some((element) => element.code === categoryCode),
      )
      if (!categories) {
        return
      }
      const indexOfCurrentCategory = categories.indexOf(categories.find((category) => category.code === categoryCode)!)
      const categoriesMapped = categories.slice(0, indexOfCurrentCategory + 1).map((category) => category.label)
      const query = {}
      const slug = getSlugFromCategories(categoriesMapped)
      if (supplierName) {
        query[SUPPLIER_REFINEMENT] = supplierName
        store.dispatch('setSelectedSupplier', { name: supplierName })
      }
      router?.push({
        name: 'category',
        query,
        params: { slug },
      })
    }

    function searchActiveSubstance(activeSubstance) {
      router?.push({
        name: 'search',
        query: { 'refinementList[active_substances][0]': activeSubstance },
      })
    }

    async function fetchSupplierRelatedProducts(prd) {
      if (prd?.supplier && !enrichedSupplierRelatedProducts.value?.length) {
        const nbItemsToFetch = 18
        store.dispatch('shop/fetchSupplierRelatedProducts', { product: prd, nbItemsToFetch })
      }
    }

    async function fetchAlgoliaProducts(prd) {
      if (!algoliaProducts.value?.length) {
        await store.dispatch('shop/fetchAlgoliaProducts', { productIds: [prd._id] })
      }
    }

    async function fetchSimilarProducts(prd) {
      if (!similarProducts.value?.length) {
        const nbItemsToFetch = 18
        store.dispatch('shop/fetchSimilarProducts', { product: prd, nbItemsToFetch })
      }
    }

    async function fetchFrequentlyBoughtTogetherProducts(prd) {
      if (!frequentlyBoughtTogetherProducts.value?.length) {
        const nbItemsToFetch = 18
        store.dispatch('shop/fetchFrequentlyBoughtTogetherProducts', { product: prd, nbItemsToFetch })
      }
    }

    async function getBanner() {
      banner.value = await getBannersForProductSheetUseCase.execute(
        isAuthenticated.value,
        currentCooperativeId.value,
        product.value?.categories,
        product.value?.supplier,
        product.value?._id,
        userSegments.value,
      )
    }

    async function fetchArticleThumbnail(supplier, isAuth, partnerId) {
      if (!supplier || articleThumbnail.value) return
      try {
        const supplierToTag = articleUseCase.convertKeywordToTag(supplier)
        const queryBySupplierTag = {
          title: '',
          articles: [],
          highlighted: false,
          isManual: false,
          tags: [supplierToTag],
        }
        let articles = await articleUseCase.getArticles(queryBySupplierTag, isAuth, partnerId)
        if (articles.length === 0) {
          articleThumbnail.value = null
          return
        }
        articles = articles.map((article) => ({
          ...article,
          header: {
            ...article.header,
            richBoxTag: '',
          },
        }))

        articleThumbnail.value = {
          title: `${i18n._('Les dernières actus')} ${supplier}`,
          description: '',
          backgroundColor: '',
          anchorLabel: '',
          articles,
          highlighted: false,
          isManual: true,
        }
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('Error fetching article', err)
      }
    }

    function sendProductToAnalytics({
      prd,
      algoliaPrd,
    }: {
      prd?: FullProduct<CooperativeProduct>
      algoliaPrd?: FullProduct<ProductHit>
    }) {
      if (!prd || !algoliaPrd) {
        return
      }
      sendProductViewedToAnalytics(prd, algoliaPrd)
      productViewedEventFired.value = true
    }

    watch(product, async (newProduct) => {
      await fetchAlgoliaProducts(newProduct)
      if (hasVerifiedSlugRedirection.value && !productViewedEventFired.value && algoliaProducts.value) {
        sendProductToAnalytics({ prd: newProduct, algoliaPrd: algoliaProducts.value[0] })
      }
      await fetchSupplierRelatedProducts(newProduct)
      if (isAuthenticated.value) {
        await fetchSimilarProducts(newProduct)
      }
      await fetchFrequentlyBoughtTogetherProducts(newProduct)
      await fetchArticleThumbnail(newProduct?.supplier, isAuthenticated.value, currentCooperativeId.value)
    })

    watch([currentCooperativeId, currentAddress], async () => {
      await getProduct()
    })

    function verifyProductSlugAndRedirect() {
      if (product.value && product.value.slug) {
        const currentSlug = route.params.productSlug as string
        if (currentSlug !== product.value.slug) {
          router?.replace({
            name: 'product',
            params: { productSlug: product.value.slug },
          })
          return false
        }
      }
      hasVerifiedSlugRedirection.value = true
      if (product.value && !productViewedEventFired.value && algoliaProducts.value) {
        sendProductToAnalytics({ prd: product.value, algoliaPrd: algoliaProducts.value[0] })
      }
      return true
    }

    onBeforeMount(async () => {
      await getProduct()
      hasNoRedirection.value = verifyProductSlugAndRedirect()
      updateVisitedProductsList()
      await getBanner()
    })

    onUpdated(() => {
      if (route.hash) {
        const computedOffset = window.innerWidth >= 992 ? -130 : -120
        VueScrollTo.scrollTo(route.hash, 0, { offset: computedOffset })
      }
    })

    onUnmounted(() => {
      hasVerifiedSlugRedirection.value = false
      productViewedEventFired.value = false
    })

    async function onSendProductInfoRequest(payload: ProductInfoRequestPayload) {
      return await sendProductInfoRequestUseCase.sendRequest(currentCooperativeId.value ?? '', payload.to.id, payload)
    }

    provideSendProductInfoRequest(onSendProductInfoRequest)

    useHead(() => {
      const { metaInfo } = useRedirectUrl()
      const { meta } = metaInfo() || { meta: [] }

      const hreflangLinks: Array<{ rel: string; hreflang: string; href: string }> = []

      const frontUrl = getConfig('FRONT_URL') || ''

      if (product.value?.available_slugs && Object.values(product.value.available_slugs).length > 1) {
        hreflangLinks.push({
          rel: 'alternate',
          hreflang: 'x-default',
          href: `${frontUrl}/p/${Object.values(product.value.available_slugs)[0]}`,
        })

        Object.entries(product.value.available_slugs).forEach(([lang, slug]) => {
          const [language, region] = lang.split('-')

          hreflangLinks.push({
            rel: 'alternate',
            hreflang: lang,
            href: `${frontUrl}/${region.toLowerCase()}/${language.toLowerCase()}/p/${slug}`,
          })
        })
      } else if (product.value?.slug) {
        hreflangLinks.push({
          rel: 'alternate',
          hreflang: 'x-default',
          href: `${frontUrl}/p/${product.value.slug}`,
        })
      }

      return {
        title: productSEOTitle(product.value, currentAppBrand),
        meta: meta.concat([
          {
            vmid: 'description',
            name: 'description',
            // @ts-ignore revoir la conception de la méthode
            content: productSEODescription(product.value, currentAppBrand),
          },
        ]),
        link: hreflangLinks,
      }
    })

    return {
      articleThumbnail,
      currentCooperativeId,
      currentCooperativeName,
      currentProfile,
      enrichedSupplierRelatedProducts,
      error,
      isAuthenticated,
      isCropProtectionAllowed,
      isExplosivesPrecursorAllowed,
      productId,
      sendProductViewedToAnalytics,
      supplierRelatedProductCount,
      supplierRelatedProducts,
      user,
      similarProducts,
      frequentlyBoughtTogetherProducts,
      getProduct,
      product,
      login,
      goToProductSearch,
      searchActiveSubstance,
      fetchArticleThumbnail,
      i18n,
      hasNoRedirection,
      banner,
    }
  },
  async beforeUpdate() {
    const { beforeRouteEnter } = useRedirectUrl()
    await beforeRouteEnter({ fullPath: this.$route.fullPath }, {}, (params) => params && this.$router.replace(params))
  },
})
</script>
<style lang="scss">
.product__push-article {
  --idf-carousel-padding: 0;

  margin-top: 3rem;
}
</style>
